import apiClient from "../authentication/utils/core/axios";

/**
 * AdminAPI — ADMIN DASHBOARD API UTIL
 * Handles health checks, user operations, bulk actions, export, and stats
 */
const AdminAPI = {
  /**
   * Health check status for the admin panel
   * @returns {Promise<Object>}
   */
  health: async () => {
    const response = await apiClient.get('/admin/health');
    if (response.status !== 200) throw new Error('Admin health check failed');
    return response.data;
  },

  /**
   * Get details for a specific user by user_id
   * @param {number} userId
   * @returns {Promise<Object>}
   */
  fetchUser: async (userId) => {
    const response = await apiClient.get(`/admin/users/${userId}`);
    if (response.status !== 200) throw new Error('Failed to fetch user details');
    return response.data;
  },

  /**
   * Perform a bulk user operation (ban, activate, suspend, promote, demote)
   * @param {Object} bulkData - { action: string, user_ids: number[] }
   * @returns {Promise<Object>}
   */
  bulkUserAction: async (bulkData) => {
    const response = await apiClient.post('/admin/users/bulk-action', bulkData);
    if (response.status !== 200 && response.status !== 201)
      throw new Error('Bulk user action failed');
    return response.data;
  },

  /**
   * Permanently erase another user's account (GDPR Article 17). Immediate,
   * no grace period -- distinct from the self-service deletion flow a user
   * runs on their own account. confirm_username must match the target
   * account's current username; the backend rejects a mismatch so a stale
   * list or wrong-row click can't silently erase the wrong person.
   * @param {number} userId
   * @param {Object} params - { reason: string, confirm_username: string }
   * @returns {Promise<Object>}
   */
  eraseUser: async (userId, { reason, confirm_username }) => {
    try {
      const response = await apiClient.post(`/admin/users/${userId}/erase`, { reason, confirm_username });
      if (response.status !== 200) throw new Error('Failed to erase user account');
      return response.data;
    } catch (err) {
      // Surface the backend's actual detail (e.g. the confirm_username
      // mismatch message) instead of axios's generic "Request failed with
      // status code 400" -- this is the one error message where the admin
      // genuinely needs to read the real reason, not just retry.
      const detail = err.response?.data?.detail;
      throw detail ? new Error(detail) : err;
    }
  },

  /**
   * Export data (users or audit logs) in csv or json format
   * @param {Object} exportRequest - { entity: string, format: string }
   * @returns {Promise<Blob>} - Gzipped file data for download
   */
  exportData: async (exportRequest) => {
    const response = await apiClient.post('/admin/export/data', exportRequest, {
      responseType: 'blob', // so you can trigger download
    });
    if (response.status !== 200)
      throw new Error('Export data failed');
    return response.data;
  },

  /**
 * Get list of users with pagination
 * @param {Object} params - { page: number, limit: number, search?: string }
 * @returns {Promise<Object>}
 */
  fetchUsersList: async (params = {}) => {
    const query = new URLSearchParams({
      page: String(params.page ?? 1),
      limit: String(params.limit ?? 50),
    });
    const q = params.q ?? params.search;   // <-- accept both
    if (q) query.append('q', q);            // <-- backend expects q
    const res = await apiClient.get(`/admin/users?${query.toString()}`);
    if (res.status !== 200) throw new Error('Failed to fetch users list');
    return res.data;
  },

  /**
   * Get production system statistics
   * @returns {Promise<Object>}
   */
  fetchSystemStats: async () => {
    const response = await apiClient.get('/admin/stats/system');
    if (response.status !== 200) throw new Error('Failed to fetch system stats');
    return response.data;
  },

    /** 1. Fetch model registry */
  fetchImportModels: async () => {
    const response = await apiClient.get('/admin/import/models');
    if (response.status !== 200) throw new Error('Failed to load models');
    return response.data;
  },

  /** 2. Validate a CSV upload */
  validateCSV: async ({ file, target_models, field_mapping }) => {
    if (!file || !(file instanceof File)) {
      console.warn('file is missing or not a File:', file);
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_models', target_models);
    formData.append('field_mapping', JSON.stringify(field_mapping));
    const response = await apiClient.post('/admin/import/validate-csv', formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.status !== 200) throw new Error('Validation failed');
    return response.data;
  },

  /** 3. Import a CSV file */
  importCSV: async ({ file, target_models, field_mapping }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('target_models', target_models);
  formData.append('field_mapping', JSON.stringify(field_mapping));
  const response = await apiClient.post('/admin/import/csv', formData, {
    
    withCredentials: true, // if you use cookies/csrf
  });
  if (response.status !== 200) throw new Error('Import failed');
  return response.data;
  },

  fetchAuditLogs: async (params = {}) => {
  // Support pagination and optional search
  const { page = 1, limit = 50, search } = params;
  const searchParams = new URLSearchParams();
  searchParams.append('page', page);
  searchParams.append('limit', limit);
  if (search) searchParams.append('search', search);

  const res = await apiClient.get(`/admin/audit-logs?${searchParams.toString()}`);
  return res.data;
  },
};





export default AdminAPI;
